<template>
  <div>
    <ValidationObserver
      ref="observer"
      v-slot="{ valid, dirty }"
    >
      <v-row>
        <v-col>
          <VDatePicker
            v-model="data.date"
            :label="$t('forms.labels.date')"
            :clearable="true"
            outlined
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <VTextField
            v-model="data.code"
            :label="$t('forms.labels.code')"
            :rules="{ required: false, min: 1, max: 50 }"
            :disabled="!isEditable"
            outlined
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <VSelect
            v-model="data.type"
            :disabled="!isEditable"
            :label="$t('sites.document.type')"
            :rules="{ required: true }"
            :items="types"
            :loading="loading"
            data-cy="documentForm_type"
            :validateAtInitialization="isEdition"
            outlined
          />
        </v-col>
      </v-row>

      <div>
        <v-row>
          <v-col>
            <VTextField
              v-model="data.name[selectedLang]"
              :label="$t(`forms.labels.name_${selectedLang}`)"
              :rules="{required: teamMainLang === selectedLang, max: 200 }"
              :disabled="!isEditable"
              outlined
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <VTextarea
              v-model="data.description[selectedLang]"
              :label="$t(`forms.labels.description_${selectedLang}`)"
              :rules="{ required: teamMainLang === selectedLang, max: 1000 }"
              :disabled="!isEditable"
              outlined
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <p>{{ $t('forms.labels.file') }}</p>
          </v-col>
        </v-row>
        <v-row v-if="!fileIsPresent">
          <v-col>
            <VFileDragAndDrop
              :file="data.files[selectedLang]"
              :mimes="validationFileRules.mimes"
              :disabled="!isEditable"
              autoUpload
              :loading="uploadFileLoading"
              @onChange="onChangeFile"
            />
          </v-col>
        </v-row>
        <v-row style="padding: 0px 30px 10px 30px !important">
          <UploadedBackendFiles
            v-if="fileIsPresent"
            :backendFiles="uploadedFiles"
            :type="'appendix'"
            v-on="$listeners"
            @removeFiles="removeFile"
          />
        </v-row>
      </div>
      <SlotScopeWatcher
        :value="dirty"
        @change="$emit('dirtyChanged', dirty)"
      />
      <SlotScopeWatcher
        :value="valid"
        @change="$emit('validChanged', valid)"
      />
    </ValidationObserver>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ValidationObserver } from 'vee-validate';
import FormFilesMixin from '@/mixins/FormFilesMixin';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import VDatePicker from '@/components/shared/widgets/forms/VuetifyInputs/VDatePicker.vue';
import VTextarea from '@/components/shared/widgets/forms/VuetifyInputs/VTextarea.vue';
import VFileDragAndDrop from '@/components/shared/widgets/forms/VuetifyInputs/VFileDragAndDrop';
import UploadedBackendFiles from '@/components/shared/UploadedBackendFiles.vue';


const { pick } = require('lodash');

export default {
    components: {
        SlotScopeWatcher,
        VSelect,
        VTextField,
        VDatePicker,
        VTextarea,
        VFileDragAndDrop,
        ValidationObserver,
        UploadedBackendFiles,
    },
    mixins: [
        FormFilesMixin,
    ],
    props: {
        isEditable: {
            type: Boolean,
            default: true,
        },
        editForm: {
            type: Boolean,
            default: false,
        },
        populateWith: {
            type: Object,
            default: () => ({ empty: true }),
        },
    },
    data() {
        return {
            uploadFileLoading: false,
            loading: false,
            documentTypes: [],
            uploadedFiles: [],
            data: {
                date: '',
                code: '',
                description: {},
                files: {},
                file: {},
                name: { },
                type: null,
            },
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'selectedLang',
            'teamLangs',
            'teamMainLang',
        ]),
        types() {
            return this.documentTypes.map(({ id, name }) => ({
                id,
                name: name[this.userLang] || name[this.teamMainLang],
            }));
        },
        validationFileRules() {
            return {
                mimes: [
                    'image/jpeg',
                    'image/png',
                    'application/pdf',
                ],
                size: 50 * 1024,
            };
        },
        /**
         * @return {Boolean}
         */
        isEdition() {
            return !this.populateWith.empty;
        },
        fileIsPresent() {
            return !!this.data.files[this.selectedLang];
        },

    },
    watch: {
        data: {
            async handler() {
                this.updateData();
            },
            deep: true,
            immediate: true,
        },
        selectedLang: {
            handler() {
                if (this.isEdition) this.getFile();
            },
            deep: true,
            immediate: true,
        },
    },
    async mounted() {
        await this.getTypes();
        await this.getDatas();
        if (this.isEdition) this.getFile();
    },
    methods: {
        async getTypes() {
            this.loading = true;
            const { data } = await this.$rq.getDocumentTypes({ pagination: false });
            this.documentTypes = data;
            this.loading = false;
        },
        async getDatas() {
            if (this.editForm) this.data = await this.prepareEditData(this.populateWith);
            if (!this.data?.files) {
                this.data.files = {};
            }
            if (!this.data?.file) {
                this.data.file = {};
            }
        },

        async onChangeFile(file) {
            // file bigger than 25MB
            if (file.size > 26214400) {
                this.data.files = {};
                this.$toasted.global.toast_error({
                    message: this.$t('sites.document.fileSizeExceeded'),
                });
                return;
            }

            if (file.size === 0) {
                this.data.files = {};
                this.$toasted.global.toast_error({
                    message: this.$t('sites.document.fileSizeZero'),
                });
                return;
            }

            this.data.files = {
                [this.selectedLang]: file,
            };
            await this.getInfosForFileInfo();
        },

        async getInfosForFileInfo() {
            // Liste des propriétés à extraire de l'objet
            const propertiesToExtract = ['name', 'size', 'type', 'id'];
            const fileData = await pick(this.data.files[this.selectedLang], propertiesToExtract);
            this.data.file = {
                fileName: fileData.name,
                size: fileData.size,
                format: fileData.type,
            };
            this.uploadedFiles = [{ ...this.data.file, link: { ...this.data.file } }];
            this.updateData();
        },

        async removeFile() {
            this.data.files = {
                [this.selectedLang]: '',
            };
            this.data.file = {};
            if (this.data.originalFiles[this.selectedLang]) delete this.data.originalFiles[this.selectedLang];
            await this.getInfosForFileInfo();
        },
        getFileByLang(files, lang) {
            const fileObj = files.find(item => item.lang === lang);
            if (fileObj) {
                return fileObj.file;
            }
            return {};
        },

        getFile() {
            this.getInfosForFileInfo();
            this.data.file = this.getFileByLang(this.populateWith.files, this.selectedLang);
        },
    },
};
</script>
